export const PLEDGE_SYMBOLS = ['USDT', 'USDC', 'BTC', 'ETH']
export const PLEDGE_MULTIPLES = [3, 5, 10]
export const PLEDGE_DAYS = [7, 15, 30]

export const PLEDGE_STATE_KEYS = {
  0: 'pendingReview',
  1: 'inProgress',
  2: 'overdue',
  3: 'settled',
  4: 'rejected'
}

export function isPledgeOrderInProgress(order = {}) {
  return PLEDGE_STATE_KEYS[Number(order.state)] === 'inProgress'
}

const PLEDGE_CURRENCY_NAMES = {
  USDT: 'Tether USD',
  USDC: 'USD Coin',
  BTC: 'Bitcoin',
  ETH: 'Ethereum'
}

function resolveSymbol(item) {
  const symbol = String(item.marketSymbol || item.symbol || item.coin || '').trim().toUpperCase()
  if (PLEDGE_SYMBOLS.includes(symbol)) return symbol

  const name = String(item.marketName || item.name || '').trim().toUpperCase()
  if (name.includes('TETHER') || name.includes('USDT')) return 'USDT'
  if (name.includes('USD COIN') || name.includes('USDC')) return 'USDC'
  if (name.includes('BITCOIN') || name === 'BTC') return 'BTC'
  if (name.includes('ETHEREUM') || name === 'ETH') return 'ETH'
  return ''
}

function toNumber(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export function formatPledgeNumber(value) {
  if (value === null || value === undefined || value === '') return '—'
  const text = String(value)
  if (!Number.isFinite(Number(value))) return text
  return text.replace(/(?:\.0*|(\.\d+?)0+)$/, '$1')
}

export function formatPledgeTime(value) {
  if (value === null || value === undefined || value === '') return '—'

  let date
  const numeric = Number(value)
  if (Number.isFinite(numeric)) {
    if (numeric <= 0) return '—'
    date = new Date(numeric < 10000000000 ? numeric * 1000 : numeric)
  } else {
    date = new Date(value)
  }

  if (Number.isNaN(date.getTime())) return String(value)

  const pad = number => `${number}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function formatPledgeIcon(src) {
  if (!src) return ''
  const value = String(src)
  if (/^(https?:|data:|blob:)/i.test(value)) return value
  return `${process.env.VUE_APP_IMG || ''}${value}`
}

export function getPledgeAssets(list) {
  if (!Array.isArray(list)) return []

  return list.reduce((assets, item) => {
    const symbol = resolveSymbol(item)
    const marketId = item.marketId ?? item.id
    if (!symbol || marketId === null || marketId === undefined) return assets

    const decimal = Math.max(0, Math.min(18, Math.floor(toNumber(item.decimalNum ?? item.decimal, 8))))
    const available = Math.max(0, toNumber(item.active ?? item.available ?? item.balance, 0))
    assets.push({
      ...item,
      marketId,
      marketName: item.marketName || PLEDGE_CURRENCY_NAMES[symbol],
      marketSymbol: symbol,
      icon: item.icon || item.marketIcon || '',
      available,
      decimal
    })
    return assets
  }, [])
}

export function readPledgePage(response) {
  let data = response && response.data !== undefined ? response.data : response
  if (data && data.data && !Array.isArray(data.data) && !data.list && !data.records && !data.rows) {
    data = data.data
  }
  if (Array.isArray(data)) return { list: data, total: data.length }

  const list = data && (data.list || data.records || data.rows || data.items)
  const safeList = Array.isArray(list) ? list : []
  const total = Number(data && (data.total ?? data.count ?? safeList.length))
  return { list: safeList, total: Number.isFinite(total) ? total : safeList.length }
}

export function normalizePledgeOrder(item = {}) {
  const stateNumber = Number(item.state)
  return {
    ...item,
    state: Number.isFinite(stateNumber) ? stateNumber : item.state,
    marketName: item.marketName || item.marketSymbol || '—',
    marketSymbol: item.marketSymbol || '',
    marketIcon: formatPledgeIcon(item.marketIcon || item.icon),
    createTimeText: formatPledgeTime(item.createTime),
    reviewTimeText: formatPledgeTime(item.reviewTime),
    expireTimeText: formatPledgeTime(item.expireTime),
    repayTimeText: formatPledgeTime(item.repayTime),
    amountText: formatPledgeNumber(item.amount),
    totalAmountText: formatPledgeNumber(item.totalAmount),
    unpayAmountText: formatPledgeNumber(item.unpayAmount),
    multipleText: formatPledgeNumber(item.multiple),
    pledgeDayText: formatPledgeNumber(item.pledgeDay)
  }
}

export function getPledgeLoanCopy(t) {
  return {
    pendingReview: t('pledgeLoanPendingReview'),
    overdue: t('pledgeLoanOverdue'),
    name: t('pledgeLoanName'),
    interest: t('pledgeLoanInterest'),
    maturityDate: t('pledgeLoanMaturityDate'),
    heroTitle: t('pledgeLoanHeroTitle'),
    currency: t('pledgeLoanCurrency'),
    applicationDate: t('pledgeLoanApplicationDate'),
    collateralTerm: t('pledgeLoanCollateralTerm'),
    heroSubtitle: t('pledgeLoanHeroSubtitle'),
    rejected: t('pledgeLoanRejected'),
    borrowAmount: t('pledgeLoanBorrowAmount'),
    reviewDate: t('pledgeLoanReviewDate'),
    withdrawalWarning: t('pledgeLoanWithdrawalWarning'),
    repayment: t('pledgeLoanRepayment'),
    unpayAmount: t('pledgeLoanUnpayAmount'),
    repayTime: t('pledgeLoanRepayTime'),
    collateralAmount: t('pledgeLoanCollateralAmount'),
    collateralRatio: t('pledgeLoanCollateralRatio'),
    inProgress: t('pledgeLoanInProgress'),
    settled: t('pledgeLoanSettled'),
    applyNow: t('pledgeLoanApplyNow'),
    finishedText: t('pledgeLoanFinishedText'),
    previewOnly: t('pledgeLoanPreviewOnly'),
  }
}

